import anime from 'animejs';
import { live } from '../data/live';
import { jukebox } from '../audio/jukebox';
import { fizzle } from '../renderers/fizzle';

var dayjs = require('dayjs');

/**
 * Portal's "Still Alive", sung across the break screen's schedule filler.
 *
 * DORMANT ON PURPOSE — it is expected back. `startSong()` returns before it does
 * anything, and nothing else calls into this module. It lives here rather than in
 * overlay-renderer.js because at 1100 lines it was half that file: the lyric table
 * alone is 600 lines and the ASCII art another 400.
 *
 * How it worked: the break screen's end-of-schedule filler renders four `.fizzle`
 * spans per empty row. With exactly six such rows the fizzle watcher handed over to
 * the song instead of scrambling them, and each lyric line was typed into those spans
 * in time with the track, with ASCII frames drawn into `#song-ascii`.
 *
 * To revive it: drop the `return` in startSong(), restore the mp3 upload the playBgm()
 * call points at, and set SONG_DATE_AVAILABLE in .env to the night it should fire.
 */
export const stillAlive = {
    lyrics: [
        [
            {
                text: [
                    "THIS",
                    "WAS",
                    "A",
                    "TRIUMPH"
                ],
                delayForNextRow: 3763,
            },
            {
                text: [
                    "I'M",
                    "MAKING",
                    "A NOTE",
                    "HERE"
                ],
                delayForNextRow: 2221,
            },
            {
                text: [
                    "HUGE",
                    "SUCCESS.",
                    "IT'S HARD",
                    "TO OVER-"
                ],
                delayForNextRow: 4773,
            },
            {
                text: [
                    "STATE",
                    "MY",
                    "SATIS",
                    "FACTION"
                ],
                delayForNextRow: 5238,
                function: 'fadeOutAdBreak',
            },
        ],
        [
            {
                text: [
                    "APER",
                    "TURE",
                    "SCIE",
                    "NCE"
                ],
                delayForNextRow: 3749,
                ascii: 'aperture',
            },
            {
                text: [
                    "WE DO",
                    "WHAT WE",
                    "MUST, BECAUSE",
                    "WE CAN"
                ],
                delayForNextRow: 5272,
            },
            {
                text: [
                    "FOR THE",
                    "GOOD OF",
                    "ALL OF",
                    "US"
                ],
                delayForNextRow: 3240,
            },
            {
                text: [
                    "EXCEPT",
                    "THE ONES",
                    "WHO ARE",
                    "DEAD"
                ],
                delayForNextRow: 2236,
                ascii: 'nuclear',
            },
        ],
        [
            {
                text: [
                    "BUT",
                    "THERE'S",
                    "NO",
                    "SENSE"
                ],
                delayForNextRow: 1512,
                ascii: 'aperture',
            },
            {
                text: [
                    "CRYING",
                    "OVER",
                    "EVERY",
                    "MISTAKE"
                ],
                delayForNextRow: 2498,
            },
            {
                text: [
                    "YOU",
                    "JUST",
                    "KEEP",
                    "ON TRYING"
                ],
                delayForNextRow: 1994,
            },
            {
                text: [
                    "TILL YOU",
                    "RUN OUT",
                    "OF",
                    "CAKE"
                ],
                delayForNextRow: 2115,
            },
        ],
        [
            {
                text: [
                    "AND THE",
                    "SCIENCE",
                    "GETS",
                    "DONE"
                ],
                delayForNextRow: 1902,
                ascii: 'atom',
            },
            {
                text: [
                    "AND YOU",
                    "MAKE A",
                    "NEAT",
                    "GUN"
                ],
                delayForNextRow: 1986,
            },
            {
                text: [
                    "FOR THE",
                    "PEOPLE",
                    "WHO",
                    "ARE"
                ],
                delayForNextRow: 1854,
                ascii: 'aperture',
            },
            {
                text: [
                    "STI",
                    "LL",
                    "AL",
                    "IVE"
                ],
                delayForNextRow: 7393,
            },
        ],
        [
            {
                text: [
                    "I'M",
                    "NOT",
                    "EVEN",
                    "ANGRY"
                ],
                delayForNextRow: 4225,
                ascii: 'isc'
            },
            {
                text: [
                    "I'M",
                    "BEING",
                    "SO SINCERE",
                    "RIGHT NOW"
                ],
                delayForNextRow: 5022,
            },
            {
                text: [
                    "EVEN",
                    "THOUGH",
                    "YOU BROKE",
                    "MY HEART"
                ],
                delayForNextRow: 3505,
                ascii: 'heart',
            },
            {
                text: [
                    "AND",
                    "KIL",
                    "LED",
                    "ME"
                ],
                delayForNextRow: 3230,
            },
        ],
        [
            {
                text: [
                    "AND",
                    "TORE ME",
                    "TO",
                    "PIECES"
                ],
                delayForNextRow: 3993,
                ascii: 'explosion',
            },
            {
                text: [
                    "AND THREW",
                    "EVERY PIECE",
                    "INTO",
                    "A FIRE"
                ],
                delayForNextRow: 5278,
                ascii: 'fire',
            },
            {
                text: [
                    "AS THEY",
                    "BURNED",
                    "IT HURT",
                    "BECAUSE"
                ],
                delayForNextRow: 3240,
            },
            {
                text: [
                    "I WAS",
                    "SO",
                    "HAPPY",
                    "FOR YOU"
                ],
                delayForNextRow: 2245,
                ascii: 'ok',
            },
        ],
        [
            {
                text: [
                    "NOW THESE",
                    "POINTS",
                    "OF",
                    "DATA"
                ],
                delayForNextRow: 2004,
            },
            {
                text: [
                    "MAKE",
                    "A",
                    "BEAUTIFUL",
                    "LINE"
                ],
                delayForNextRow: 2018,
                ascii: 'pingui',
            },
            {
                text: [
                    "AND",
                    "WE'RE",
                    "OUT OF",
                    "BETA"
                ],
                delayForNextRow: 1984,
            },
            {
                text: [
                    "WE'RE",
                    "RELEASING",
                    "ON",
                    "TIME"
                ],
                delayForNextRow: 1994,
            },
        ],
        [
            {
                text: [
                    "SO I'M",
                    "GLAD",
                    "I GOT",
                    "BURNED"
                ],
                delayForNextRow: 2023,
                ascii: 'explosion',
            },
            {
                text: [
                    "THINK",
                    "OF ALL",
                    "THE THINGS",
                    "WE LEARNED"
                ],
                delayForNextRow: 1994,
                ascii: 'atom',
            },
            {
                text: [
                    "FOR THE",
                    "PEOPLE",
                    "WHO",
                    "ARE"
                ],
                delayForNextRow: 1757,
                ascii: 'aperture',
            },
            {
                text: [
                    "STI",
                    "LL",
                    "AL",
                    "IVE"
                ],
                delayForNextRow: 7726,
            },
        ],
        [
            {
                text: [
                    "GO",
                    "AHEAD",
                    "AND",
                    "LEAVE ME"
                ],
                delayForNextRow: 4012,
                ascii: 'noTasto',
            },
            {
                text: [
                    "I THINK",
                    "I PREFER",
                    "TO STAY",
                    "INSIDE"
                ],
                delayForNextRow: 4978,
            },
            {
                text: [
                    "MAYBE",
                    "YOU'LL",
                    "FIND",
                    "SOMEONE ELSE"
                ],
                delayForNextRow: 3534,
                ascii: 'isc',
            },
            {
                text: [
                    "TO",
                    "HE",
                    "LP",
                    "YOU?"
                ],
                delayForNextRow: 3476,
            },
        ],
        [
            {
                text: [
                    "MAYBE",
                    "BLACK",
                    "MESA",
                    "?"
                ],
                delayForNextRow: 3761,
                ascii: 'blackMesa',
            },
            {
                text: [
                    "THAT WAS",
                    "A JOKE",
                    "HAHA",
                    "FAT CHANCE"
                ],
                delayForNextRow: 5254,
            },
            {
                text: [
                    "ANYWAY",
                    "THIS",
                    "CAKE",
                    "IS GREAT"
                ],
                delayForNextRow: 3235,
                ascii: 'cake',
            },
            {
                text: [
                    "IT'S SO",
                    "DELICIOUS",
                    "AND",
                    "MOIST"
                ],
                delayForNextRow: 2255,
            },
        ],
        [
            {
                text: [
                    "LOOK AT",
                    "ME",
                    "STILL",
                    "TALKING"
                ],
                delayForNextRow: 2008,
                ascii: 'atom',
            },
            {
                text: [
                    "WHEN",
                    "THERE'S",
                    "SCIENCE",
                    "TO DO"
                ],
                delayForNextRow: 2245,
            },
            {
                text: [
                    "WHEN I",
                    "LOOK",
                    "OUT",
                    "THERE"
                ],
                delayForNextRow: 1733,
                ascii: 'aperture',
            },
            {
                text: [
                    "IT MAKES",
                    "ME GLAD",
                    "I'M NOT",
                    "YOU"
                ],
                delayForNextRow: 2042,
            },
        ],
        [
            {
                text: [
                    "I'VE",
                    "EXPERIMENTS",
                    "TO",
                    "RUN"
                ],
                delayForNextRow: 1960,
                'ascii': 'atom',
            },
            {
                text: [
                    "THERE IS",
                    "RESEARCH",
                    "TO",
                    "BE DONE"
                ],
                delayForNextRow: 2023,
                ascii: 'aperture',
            },
            {
                text: [
                    "ON THE",
                    "PEOPLE",
                    "WHO",
                    "ARE"
                ],
                delayForNextRow: 1743,
            },
            {
                text: [
                    "STI",
                    "LL",
                    "AL",
                    "IVE"
                ],
                delayForNextRow: 2260,
            },
        ],
        [
            {
                text: [
                    "PS: AND",
                    "BELIEVE",
                    "ME I",
                    "AM"
                ],
                delayForNextRow: 1868,
                ascii: 'isc',
            },
            {
                text: [
                    "STI",
                    "LL",
                    "AL",
                    "IVE"
                ],
                delayForNextRow: 1873,
            },
            {
                text: [
                    "PPS: I'M",
                    "DOING",
                    "SCIENCE",
                    "AND I'M"
                ],
                delayForNextRow: 1999,
            },
            {
                text: [
                    "STI",
                    "LL",
                    "AL",
                    "IVE"
                ],
                delayForNextRow: 2231,
            },
        ],
        [
            {
                text: [
                    "PPPS: I FEEL",
                    "FANTASTIC",
                    "AND",
                    "I'M"
                ],
                delayForNextRow: 1762,
                ascii: 'aperture',
            },
            {
                text: [
                    "STI",
                    "LL",
                    "AL",
                    "IVE"
                ],
                delayForNextRow: 2231,
            },
            {
                text: [
                    "WHILE",
                    "YOU'RE",
                    "DYING",
                    "I'LL BE"
                ],
                delayForNextRow: 1516,
                ascii: 'interloper1',
            },
            {
                text: [
                    "STI",
                    "LL",
                    "AL",
                    "IVE"
                ],
                delayForNextRow: 2221,
                ascii: 'aperture',
            },
        ],
        [
            {
                text: [
                    "AND WHEN",
                    "YOU'RE DEAD",
                    "I WILL",
                    "BE"
                ],
                delayForNextRow: 2008,
                ascii: 'interloper2',
            },
            {
                text: [
                    "ST",
                    "ILL",
                    "AL",
                    "IVE"
                ],
                delayForNextRow: 2023,
                ascii: 'cake',
            },
            {
                text: [
                    "GRAZIE",
                    "ISC",
                    "0xFREE",
                    "0xC0CC0"
                ],
                delayForNextRow: 2004,
            },
            {
                text: [
                    "SAVE",
                    "PIETRO",
                    "SAVE",
                    "THE WORLD"
                ],
                delayForNextRow: 4000,
                ascii: 'isc',
            },
        ]
    ],

    singLevel(level) {
        for (let currentRow = 0; currentRow < 4; currentRow++) {
            stillAlive.singRow(level, currentRow, false, true);
        }
    },

    singRow(level, row, autoAdvance = true, useTimer = false) {
        const currentRow = stillAlive.lyrics[level][row];
        console.log("song currentRow", currentRow);
        if (currentRow.ascii)
            stillAlive.showSongAscii(currentRow.ascii);

        if (currentRow.function)
            stillAlive[currentRow.function]();

        for (let currentWord = 0; currentWord < 4; currentWord++) {
            let toFizzle = fizzle.getFizzle(row + 1, currentWord + 1)
            setTimeout(function () {
                fizzle.fizzleOneWithWord(toFizzle, currentRow.text[currentWord], 7)
            }, (140 * currentWord) + (row * 2400 * (useTimer ? 1 : 0)));
        }

        if (!autoAdvance)
            return;

        row++;
        if (row >= 4) {
            row = 0;
            level++;
        }

        if (level >= stillAlive.lyrics.length) {
            console.log("song ended");

            setTimeout(function () {
                anime({
                    targets: '.break-opacity',
                    duration: 3000,
                    easing: 'linear',
                    opacity: 0,
                    complete: function () {
                        document.querySelector(".ad-container").style.display = 'block';
                        document.querySelector(".incentive-container").style.display = 'flex'
                        document.querySelector(".song-container").style.display = 'none';

                        anime({
                            targets: '.break-opacity',
                            duration: 3000,
                            easing: 'linear',
                            opacity: 1,
                        });
                    }
                });
            }, 8000);

            return;
        }

        console.log("song level, row", level, row);

        setTimeout(function () {
            stillAlive.singRow(level, row);
        }, currentRow.delayForNextRow);
    },

    showSongAscii: (asciiName) => {
        const heart = `                          .,---.
                        ,/XM#MMMX;,
                      -%##########M%,
                     -@######%  $###@=
      .,--,         -H#######$   $###M:
   ,;$M###MMX;     .;##########$;HM###X=
,/@###########H=      ;################+
-+#############M/,      %##############+
%M###############=      /##############:
H################      .M#############;.
@###############M      ,@###########M:.
X################,      -$=X#######@:
/@##################%-     +######$-
.;##################X     .X#####+,
 .;H################/     -X####+.
   ,;X##############,       .MM/
      ,:+$H@M#######M#$-    .$$=
           .,-=;+$@###X:    ;/=.
                  .,/X$;   .::,
                      .,    ..`;

        const aperture = `             .,-:;//;:=,
         . :H@@@MM@M#H/.,+%;,
      ,/X+ +M@@M@MM%=,-%HMMM@X/,
     -+@MM; $M@@MH+-,;XMMMM@MMMM@+-
    ;@M@@M- XM@X;. -+XXXXXHHH@M@M#@/.
  ,%MM@@MH ,@%=            .---=-=:=,.
  -@#@@@MX .,              -%HX$$%%%+;
 =-./@M@M$                  .;@MMMM@MM:
 X@/ -$MM/                    .+MM@@@M$
,@M@H: :@:                    . -X#@@@@-
,@@@MMX, .                    /H- ;@M@M=
.H@@@@M@+,                    %MM+..%#$.
 /MMMM@MMH/.                  XM@MH; -;
  /%+%$XHH@$=              , .H@@@@MX,
   .=--------.           -%H.,@@@@@MX,
   .%MM@@@HHHXX$$$%+- .:$MMX -M@@MM%.
     =XMMM@MM@MM#H;,-+HMM@M+ /MMMX=
       =%@M@M#@$-.=$@MM@@@M; %M%=
         ,:+$+-,/H#MMMMMMM@- -,
               =++%%%%+/:-.`;

        const nuclear = `             =+$HM####@H%;,
          /H###############M$,
          ,@################+
           .H##############+
             X############/
              $##########/
               %########/
                /X/;;+X/

                 -XHHX-
                ,######,
#############X  .M####M.  X#############
##############-   -//-   -##############
X##############%,      ,+##############X
-##############X        X##############-
 %############%          %############%
  %##########;            ;##########%
   ;#######M=              =M#######;
    .+M###@,                ,@###M+.
       :XH.                  .HX:`;

        const atom = `                 =/;;/-
                +:    //
               /;      /;
              -X        H.
.//;;;:;;-,   X=        :+   .-;:=;:;%;.
M-       ,=;;;#:,      ,:#;;:=,       ,@
:%           :%.=/++++/=.$=           %=
 ,%;         %/:+/;,,/++:+/         ;+.
   ,+/.    ,;@+,        ,%H;,    ,/+,
      ;+;;/= @.  .H##X   -X :///+;
      ;+=;;;.@,  .XM@$.  =X.//;=%/.
   ,;:      :@%=        =$H:     .+%-
 ,%=         %;-///==///-//         =%,
;+           :%-;;;;;;;;-X-           +:
@-      .-;;;;M-        =M/;;;-.      -X
 :;;::;;-.    %-        :+    ,-;;-;:==
              ,X        H.
               ;/      %=
                //    +;
                 ,////,`;

        const fire = `                     -$-
                    .H##H,
                   +######+
                .+#########H.
              -$############@.
            =H###############@  -X:
          .$##################:  @#@-
     ,;  .M###################;  H###;
   ;@#:  @###################@  ,#####:
 -M###.  M#################@.  ;######H
 M####-  +###############$   =@#######X
 H####$   -M###########+   :#########M,
  /####X-   =########%   :M########@/.
    ,;%H@X;   .$###X   :##MM@%+;:-
                 ..
  -/;:-,.              ,,-==+M########H
 -##################@HX%%+%%$%%%+:,,
    .-/H%%%+%%$H@###############M@+=:/+:
/XHX%:#####MH%=    ,---:;;;;/&&XHM,:###$
$@#MX %+;-                           .`;

        const ok = `                                     :X-
                                  :X###
                                ;@####@
                              ;M######X
                            -@########$
                          .$##########@
                         =M############-
                        +##############$
                      .H############$=.
         ,/:         ,M##########M;.
      -+@###;       =##########M;
   =%M#######;     :#########M/
-$M###########;   :########/
 ,;X###########; =#######$.
     ;H#########+######M=
       ,+#############+
          /M########@-
            ;M#####%
              +####:
               ,$M-`;

        const explosion = `            .+
             /M;
              H#@:              ;,
              -###H-          -@/
               %####$.  -;  .%#X
                M#####+;#H :M#M.
..          .+/;%#############-
 -/%H%+;-,    +##############/
    .:$M###MH$%+############X  ,--=;-
        -/H#####################H+=.
           .+#################X.
         =%M####################H;.
            /@###############+;;/%%;,
         -%###################$
       ;H######################M=
    ,%#####MH$%;+#####M###-/@####%
  :$H%+;=-      -####X.,H#   -+M##@-
 .              ,###;    ;      =$##+
                .#H,               :XH,
                 +                   .;-`;

        const blackMesa = `           .-;+$XHHHHHHX$+;-.
        ,;X@@X%/;=----=:/%X@@X/,
      =$@@%=.              .=+H@X:
    -XMX:                      =XMX=
   /@@:                          =H@+
  %@X,                            .$@$
 +@X.                               $@%
-@@,                                .@@=
%@%                                  +@$
H@:                                  :@H
H@:         :HHHHHHHHHHHHHHHHHHX,    =@H
%@%         ;@M@@@@@@@@@@@@@@@@@H-   +@$
=@@,        :@@@@@@@@@@@@@@@@@@@@@= .@@:
 +@X        :@@@@@@@@@@@@@@@M@@@@@@:%@%
  $@$,      ;@@@@@@@@@@@@@@@@@M@@@@@@$.
   +@@HHHHHHH@@@@@@@@@@@@@@@@@@@@@@@+
    =X@@@@@@@@@@@@@@@@@@@@@@@@@@@@X=
      :$@@@@@@@@@@@@@@@@@@@M@@@@$:
        ,;$@@@@@@@@@@@@@@@@@@X/-
           .-;+$XXHHHHHX$+;-.`;

        const cake = `            ,:/+/-
            /M/              .,-=;//;-
       .:/= ;MH/,    ,=/+%$XH@MM#@:
      -$##@+$###@H@MMM#######H:.    -/H#
 .,H@H@ X######@ -H#####@+-     -+H###@X
  .,@##H;      +XM##M/,     =%@###@X;-
X%-  :M##########$.    .:%M###@%:
M##H,   +H@@@$/-.  ,;$M###@%,          -
M####M=,,---,.-%%H####M$:          ,+@##
@##################@/.         :%H##@$-
M###############H,         ;HM##M$=
#################.    .=$M##M$=
################H..;XM##M$=          .:+
M###################@%=           =+@MH%
@#################M/.         =+H#X%=
=+M###############M,      ,/X#H+:,
  .;XM###########H=   ,/X#H+:;
     .=+HM#######M+/+HM@+=.
         ,:/%XM####H/.
              ,.:=-.`;

        const glados = `       #+ @      # #              M#@
 .    .X  X.%##@;# #   +@#######X. @H%
   ,==.   ,######M+  -#####%M####M-    #
  :H##M%:=##+ .M##M,;#####/+#######% ,M#
 .M########=  =@#@.=#####M=M#######=  X#
 :@@MMM##M.  -##M.,#######M#######. =  M
             @##..###:.    .H####. @@ X,
   ############: ###,/####;  /##= @#. M
           ,M## ;##,@#M;/M#M  @# X#% X#
.%=   ######M## ##.M#:   ./#M ,M #M ,#$
##/         $## #+;#: #### ;#/ M M- @# :
#+ #M@MM###M-;M #:$#-##$H# .#X @ + $#. #
      ######/.: #%=# M#:MM./#.-#  @#: H#
+,.=   @###: /@ %#,@  ##@X #,-#@.##% .@#
#####+;/##/ @##  @#,+       /#M    . X,
   ;###M#@ M###H .#M-     ,##M  ;@@; ###
   .M#M##H ;####X ,@#######M/ -M###$  -H
    .M###%  X####H  .@@MM@;  ;@#M@
      H#M    /@####/      ,++.  / ==-,
               ,=/:, .+X@MMH@#H  #####$=`;

        const isc = `
        
        
        
        
  .@@@@    -+#@@@@%#=.      .=*%@@@@%#= 
  =@@@#  =@@@@@@@@@@@@=   =%@@@@@@@@@=-:
  #@@@+ =@@@%:   .+@@#- =@@@@%=:...:-*: 
  @@@@: #@@@*      :.  *@@@%:     =%*   
 :@@@@  =@@@@@%#*=:   =@@@%     =@@-    
 +@@@*   :*@@@@@@@@@+ %@@@-    =@#.     
 %@@@-      .:-+#@@@@+%@@@+             
.@@@@. +@#.      #@@@+-@@@@+            
=@@@% @@@@@*=--+#@@@#  =@@@@@#+++*#*    
#@@@+ .+@@@@@@@@@@%-    .+@@@@@@@@@@*   `;

        const lfr = `              **.                       
             +#+                        
           -*#=   .       :             
        .-=-+#*:=#+:.   .+:             
           -+**=-.     =*-              
        .=**=.       -++=   .:          
       :**-        :++++      --        
      :*+.       .=++++.       :=       
      ++        -+++++:         -=      
     :+:      :======-          .*.     
     :+     :==========-:.       +:     
     .+      .::-==========-:.  .+:     
      =:          .:-======-:   =+      
       =.          .-====-.    -+:      
        -.        :----:     .=+-       
         .:      ----.     .-==.        
            .  .--:     .-==-.          
              :-.   ..:::..             
            .:.`;

        const mdm = `            .:--==+++++==-:.            
         :=++++---=+:=+--=+++=:         
      .=++=-:++ =- + =.-==-:++++=.      
    .=++=+-.:-=:.::+.+.:=-.=++++++=.    
   -+++= .:=.:-++++++++++-=+++++++++-   
  =++- :: --+++----------=+++++++++++=  
 -+++++-:=++=--++---:-+++=--+++-=-.+++- 
.++++++++++--++++.    .++++==++ -:-:.-+.
=+++++++++--+++++:     =:::-+++--=-:=++=
++++++++++.++             . :+-=-.:::=++
+++--::=++.++              .::-++=-+++++
=++ ==::++:=+=:...     ::-=++:+++++++++=
.++=---:-++:=++++=     -++++:=+=-=+++++:
 -++:..::=++--+++=    :+++--++-.-:.=++= 
  =++.==-.=+++----====----=+=--:::.=+=  
   -+++.:++++==++++===+++==:.=:.++++-   
    .=++:++++. +=.=:::=:.-.=-.::++=.    
      .=++++-:....-.+-.=.-:.++++=:      
        .:=+++=+-=+:::-+==+++=:.        
            .:-===+++++==-:.`;

        const bolognaNerd = `            :-=+*##%%##**=-:            
        .-*%%%%%%%%%%%%%%%%%%*=.        
      :*%%%%%%%%%%%%#*#**#*%%%%%*:      
    :*%%%%%%%%%%%%%%++++++=%%%%%%%#:    
   =%%%%%%%%%%%%%%%%%-+**=+%%%%%%%%%+   
  *%%%%%%%%%%%%%%%%#=======*%%%%%%%%%*  
 +%%%%%%%%%%%%%%%%%%+***+*+#%%%%%%%%%%* 
:%%%%%%%%%%%%%%*#%%%%=#%%+#%%%%%%%%%%%%-
*%%%%%%%%%%%%%*+***=#=#%%+#%%%%%%%%%%%%*
#%%%%%%%%%%%%%=%%+*=#-+++==%%%%%%%%%%%%%
#%%%%%%%%%%%%**%*++#++****=+%%%%%%%%%%%%
*%%%%%%%%%%%%-%%+*=%-######=%%%%%%%%%%%*
:%%%%%%%%%%#+*#:**+*=%%%%%%-#%%%%%%%%%%-
 +%%%%%%%%%=#+*#***#***+*#+%=%%%%%%%%%* 
  *%%%%%%%%=#+#+*++**+*#++*#=%%%%%%%%*  
   +%%%%%%%=* : + :+.: = + #=%%%%%%%+   
    :#%%%%%=#-#-*-=+-+-*-==%=%%%%%#-    
      -*%%%#****************#%%%#-      
        .=*%%%%%%%%%%%%%%%%%%#=.        
            :-+*##%%%%##*+=:`;

        const pingui = `                                        
         :-.                            
       .*++++--:                        
      .****+=-:.                        
  +==+***+*+:                           
 .=*++=.:=+=:-                          
   *#=-+::+++=====:                     
   =#*-.  *++*-                         
    =#+  .-=++=.                        
     .++.     .                         
       :*=.     .               =-:-==+-
        =#*-::   .-          :=++++++:  
       :=**--==.  -==-:::--==++++*+:    
    :=+===++--==-:-..=#*++++++**+:      
  .+++++++***+++-:--=.-**#**#+-         
 =*+++++*##**+++-::--..-**=:            
-#*++=++#%##*+++-::.-=::*:              
%#*+++====+++++=-:::=-::*.              
====------::::-:....::..-`;

        const noTasto = `
                          =***=:.       
                         *%#*#%%%%+     
                       .:*%%%%@@@@@%    
                       -+*#@@@@@@@@@:   
     ==:                *%@%%%@@@@@*    
 :=+==++=:              #%%@@%%@%%+     
  .++===++*++++-:::::-=+*%@%##%%%%*-.   
    .--:::+****+======++==#*+====+=---. 
             ::::-------=+*++=--::---=- 
                        -+*===+--::::-: 
                       :-**=**=::::::-: 
                       -=*##==--:::::-- 
                       =+%#+--=---:::--.
                      -=*%*=====------- 
                     .++*#+*++*==------ 
                   -*%%+***++**====-==: 
               ---=#@@%***#+***+======  
             -+**++=+*%%%#*+#*+=+====-  `;

        const interloper1 = `
                 ::-=-.                 
             .+%@@@#@@@@%*:             
           :#@@@@@@=@@@@@@@%=           
          =@@@#%@@@%=@@@@@@@@=          
          @@@@@#***#-%%#****%@          
          -@@@@@@@@@+-#%@@@@@=          
           -%@@%%%@@#+@@@@@%=           
             ++@@@+%#+@@@#=             
           .*@:+@@ %#+%-                
          .@@@%...+@-.-@:               
          .@@@@@*%@@@-:@-               
          :@@@@@@@@@@@@@=               
          :@@@@@@@@@@@%=.               
          :@@@@@@@@@@@+                 
           .+@@@@@@@@@=                 
            .@@@@@@@@@=                 
            .@@@@@@@@@-                 
            .@@@@@@@@@-                 
            .@@@@@@@@@:                 
             +++++++++.                 `;


        const interloper2 = `
░░░░░░░░░░░░░░░░░░▒▒▒▒░░░░░░░░░░░░░░░░░░
░░░░░░░░░░░░░░▓████▓█████▓▒░░░░░░░░░░░░░
░░░░░░░░░░░▒███████▒████████▒░░░░░░░░░░░
░░░░░░░░░░▒███▓█████▒████████▒░░░░░░░░░░
░░░░░░░░░░██████▓▓▓▓▒███▓▓▓▓██░░░░░░░░░░
░░░░░░░░░░▒█████████▓▒███████▒░░░░░░░░░░
░░░░░░░░░░░▒████████▓▓██████▒░░░░░░░░░░░
░░░░░░░░░░░░░▓▓███▓█▓▓████▒░░░░░░░░░░░░░
░░░░░░░░░░░░▓█▒▓██░█▓▓█▒░░░░░░░░░░░░░░░░
░░░░░░░░░░░████░░░▓█▒░▒█▒░░░░░░░░░░░░░░░
░░░░░░░░░░░█████▓████▒░█▒░░░░░░░░░░░░░░░
░░░░░░░░░░░█████████████▒░░░░░░░░░░░░░░░
░░░░░░░░░░░████████████▒░░░░░░░░░░░░░░░░
░░░░░░░░░░▒███████████▓░░░░░░░░░░░░░░░░░
░░░░░░░░░░░░▓█████████▒░░░░░░░░░░░░░░░░░
░░░░░░░░░░░░░█████████▒░░░░░░░░░░░░░░░░░
░░░░░░░░░░░░░█████████▒░░░░░░░░░░░░░░░░░
░░░░░░░░░░░░░█████████▒░░░░░░░░░░░░░░░░░
░░░░░░░░░░░░░█████████░░░░░░░░░░░░░░░░░░
░░░░░░░░░░░░░▓▓▓▓▓▓▓▓▓░░░░░░░░░░░░░░░░░░`;

        const ascii = {
            heart: heart,
            aperture: aperture,
            nuclear: nuclear,
            atom: atom,
            fire: fire,
            ok: ok,
            explosion: explosion,
            blackMesa: blackMesa,
            cake: cake,
            glados: glados,
            isc: isc,
            lfr: lfr,
            mdm: mdm,
            bolognaNerd: bolognaNerd,
            pingui: pingui,
            noTasto: noTasto,
            interloper1: interloper1,
            interloper2: interloper2,
        };

        const asciiElement = document.querySelector('#song-ascii');
        asciiElement.innerHTML = '';
        stillAlive.showNextChar(asciiElement, ascii[asciiName]);
    },

    asciiTimer: undefined,
    showNextChar: (element, text, index = 0) => {
        if (stillAlive.asciiTimer)
            clearTimeout(stillAlive.asciiTimer);

        stillAlive.asciiTimer = setTimeout(function () {
            stillAlive.appendAscii(element, text.charAt(index));
            index++;
            if (index >= text.length)
                return;

            stillAlive.appendAscii(element, text.charAt(index));
            index++;
            if (index >= text.length)
                return;

            stillAlive.showNextChar(element, text, index);
        }, 1);
    },

    appendAscii: (element, char) => {
        element.innerHTML += char;
    },

    fadeOutAdBreak: () => {
        anime({
            targets: '.break-opacity',
            duration: 3000,
            easing: 'linear',
            opacity: 0,
            complete: function () {
                document.querySelector(".ad-container").style.display = 'none';
                document.querySelector(".incentive-container").style.display = 'none'
                document.querySelector(".break-opacity").style.opacity = 1;
            }
        });
    },


    /**
     * Called by the fizzle watcher when the break screen has exactly six filler rows.
     * Everything past the early return is the dormant performance.
     */
    startSong: () => {
        if (!window.playBgm) {
            console.log("Requested song, but no playBgm function.");
            return;
        }

        console.log("Requested song.");
        console.log("Song available from", process.env.SONG_DATE_AVAILABLE, dayjs().format());
        if (!window.cake && !dayjs().isAfter(process.env.SONG_DATE_AVAILABLE)) {
            console.log("Song is not yet available.");
            return;
        }

        return; // dormant — remove to bring the song back

        if (live.shouldSendAds) {
            clearTimeout(live.adSendingTimeout);
            console.log("Clearing ad timeout in favor of song.");
        }

        setTimeout(function () {
            jukebox.playIntroChatAudio();
        }, 15000);

        setTimeout(function () {
            anime({
                targets: '#media-player audio',
                duration: 3000,
                easing: 'linear',
                volume: 0,
                complete: function () {
                    jukebox.playBgm({
                        caption: "Mike Pouch",
                        alternativeText: "RECEIVING EXTERNAL TRANSMISSION",
                        mime: "audio/mpeg",
                        url: "/uploads/Portal_Still_Alive_6cf53d1097.mp3"
                    });
                    document.querySelector("#media-player audio").volume = 0.2;

                    stillAlive.singRow(0, 0);
                }
            });
        }, 25000);
    },
};

window.stillAlive = stillAlive;
